import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cpu } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import AICostDashboard from '../components/dashboard/AICostDashboard';

const AdminAICosts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user is SUPER admin only (not regular admin)
  const isSuperAdmin = user?.role === 'super_admin' || user?.email === 'admin@homefixer360.com';

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Cpu className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Super Admin Access Required</h2>
          <p className="text-slate-400 mb-4">Only Super Administrators can view AI & platform cost data.</p>
          <Button onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-blue-400" />
                  AI & Platform Cost Monitor
                </h1>
                <p className="text-sm text-slate-400">
                  Track Emergent credits and AI usage across your deployments
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <AICostDashboard />
        
        {/* Additional Info */}
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          {/* Credit Info */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Understanding Credits</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Deployment (per app)</span>
                <span className="text-white">50 credits/month</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">AI Usage (Gemini Flash)</span>
                <span className="text-white">~0.01 credits/1K tokens</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg AI call</span>
                <span className="text-white">~360 tokens (~$0.0001)</span>
              </div>
              <div className="border-t border-slate-700 pt-3 mt-3">
                <p className="text-slate-400">
                  <strong className="text-green-400">Tip:</strong> Switching from GPT-4o to Gemini Flash 
                  saves approximately 70% on AI costs while maintaining quality.
                </p>
              </div>
            </div>
          </div>

          {/* API Endpoints */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">API Endpoints</h3>
            <div className="space-y-2 text-sm font-mono">
              <div className="bg-slate-900 rounded p-2">
                <span className="text-green-400">GET</span>
                <span className="text-slate-300 ml-2">/api/ai-usage/summary</span>
              </div>
              <div className="bg-slate-900 rounded p-2">
                <span className="text-green-400">GET</span>
                <span className="text-slate-300 ml-2">/api/ai-usage/stats</span>
              </div>
              <div className="bg-slate-900 rounded p-2">
                <span className="text-green-400">GET</span>
                <span className="text-slate-300 ml-2">/api/ai-usage/platforms</span>
              </div>
              <div className="bg-slate-900 rounded p-2">
                <span className="text-green-400">GET</span>
                <span className="text-slate-300 ml-2">/api/ai-usage/projection</span>
              </div>
              <div className="bg-slate-900 rounded p-2">
                <span className="text-green-400">GET</span>
                <span className="text-slate-300 ml-2">/api/ai-usage/multi-platform-summary</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAICosts;
